/**
 * Immich 写真/動画ライトボックス (フルスクリーン表示 + ナビゲーション)
 *
 * - 画像: JWT 認証が必要な /immich/thumbnail エンドポイントを apiClient (Bearer 付き)
 *   で Blob 取得して ObjectURL 表示。
 * - 動画: /immich/asset/{id}/playback-url で短命署名 URL を取得し
 *   ReactPlayer で再生。
 * - ←/→ キー / 左右ボタン / スワイプで前後の写真を切り替え。
 * - タップ (背景) / ESC / 閉じるボタンで閉じる。
 */

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { ChevronLeft, ChevronRight, Info, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactPlayer from 'react-player'
import { toast } from 'sonner'

import { apiClient } from '@/api/client'
import {
  getImmichAssetMetadata,
  getImmichVideoPlaybackUrl,
  type ImmichAsset,
  type ImmichAssetMetadata,
} from '@/api/services/immich'
import { parseUtcDate } from '@/lib/date-utils'
import { cn } from '@/lib/utils'

type Props = {
  assets: ImmichAsset[]
  index: number
  onIndexChange: (index: number) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 左上に出す、表示中の写真に対する操作 */
  renderActions?: (asset: ImmichAsset) => React.ReactNode
}

/** スワイプ判定の閾値 (px) */
const SWIPE_THRESHOLD = 50

/** Immich の動画時間 (ミリ秒) を h:mm:ss または m:ss で表示する */
const formatAssetDuration = (durationMs: number): string => {
  const totalSeconds = Math.floor(durationMs / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (value: number) => String(value).padStart(2, '0')

  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
}

export const PhotoLightbox = ({ assets, index, onIndexChange, open, onOpenChange, renderActions }: Props) => {
  const current = assets.at(index) ?? null
  const currentId = current?.id ?? null
  const isVideo = current?.type === 'VIDEO'
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const [assetMetadata, setAssetMetadata] = useState<ImmichAssetMetadata | null>(null)
  const [metadataLoading, setMetadataLoading] = useState(false)
  const [metadataFailed, setMetadataFailed] = useState(false)
  const touchStartX = useRef<number | null>(null)
  const [showInfo, setShowInfo] = useState(false)

  useEffect(() => {
    if (!open) setShowInfo(false)
  }, [open])

  useEffect(() => {
    if (!showInfo || !currentId) {
      setAssetMetadata(null)
      setMetadataLoading(false)
      setMetadataFailed(false)
      return
    }

    let cancelled = false
    setAssetMetadata(null)
    setMetadataLoading(true)
    setMetadataFailed(false)

    getImmichAssetMetadata(currentId)
      .then((metadata) => {
        if (!cancelled) setAssetMetadata(metadata)
      })
      .catch(() => {
        if (!cancelled) setMetadataFailed(true)
      })
      .finally(() => {
        if (!cancelled) setMetadataLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [currentId, showInfo])

  const path = useMemo(
    () => (current && !isVideo ? `/immich/thumbnail/${current.id}?size=preview` : null),
    [current, isVideo],
  )

  // 画像 (IMAGE) を取得: Blob → ObjectURL
  useEffect(() => {
    if (!open || !path) return
    let cancelled = false

    setObjectUrl(null)
    setFailed(false)

    apiClient
      .get<Blob>(path, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return
        setObjectUrl(URL.createObjectURL(res.data))
      })
      .catch(() => {
        if (cancelled) return
        setFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [open, path])

  // 動画 (VIDEO) の署名 URL を取得
  useEffect(() => {
    if (!open || !currentId || !isVideo) {
      setVideoUrl(null)
      setFailed(false)
      return
    }
    let cancelled = false
    setVideoUrl(null)
    setFailed(false)

    getImmichVideoPlaybackUrl(currentId)
      .then((url) => {
        if (cancelled) return
        setVideoUrl(url)
      })
      .catch(() => {
        if (cancelled) return
        setFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [open, currentId, isVideo])

  // ObjectURL の解放
  useEffect(() => {
    if (!objectUrl) return
    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [objectUrl])

  const handlePrev = useCallback(() => {
    if (assets.length === 0) return
    onIndexChange((index - 1 + assets.length) % assets.length)
  }, [assets.length, index, onIndexChange])

  const handleNext = useCallback(() => {
    if (assets.length === 0) return
    onIndexChange((index + 1) % assets.length)
  }, [assets.length, index, onIndexChange])

  // キーボード操作
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        handlePrev()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        handleNext()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, handlePrev, handleNext])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches.item(0)
    touchStartX.current = t ? t.clientX : null
  }, [])

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const startX = touchStartX.current
      touchStartX.current = null
      if (startX === null || assets.length <= 1) return
      const endTouch = e.changedTouches.item(0)
      if (!endTouch) return
      const dx = endTouch.clientX - startX
      if (Math.abs(dx) < SWIPE_THRESHOLD) return
      if (dx > 0) handlePrev()
      else handleNext()
    },
    [assets.length, handlePrev, handleNext],
  )

  if (!current) return null

  const place = assetMetadata
    ? [assetMetadata.city, assetMetadata.state, assetMetadata.country].filter(Boolean).join(', ')
    : ''
  const hasCoordinates = typeof assetMetadata?.latitude === 'number' && typeof assetMetadata.longitude === 'number'
  const camera = assetMetadata
    ? [assetMetadata.make, assetMetadata.model, assetMetadata.lensModel].filter(Boolean).join(' / ')
    : ''
  const hasMultiple = assets.length > 1

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'fixed inset-0 z-10000 bg-black/90',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'fixed inset-0 z-10001 flex items-center justify-center outline-none',
          )}
          onClick={() => onOpenChange(false)}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <DialogPrimitive.Title className="sr-only">{current.originalFileName || '写真'}</DialogPrimitive.Title>

          {/* 画像/動画本体 (クリックで閉じるのを抑止) */}
          <div className="relative max-h-full max-w-full p-4" onClick={(e) => e.stopPropagation()}>
            {failed && <div className="text-sm text-white">読み込みに失敗しました</div>}
            {!failed && isVideo && !videoUrl && <div className="text-sm text-white/70">動画を準備中…</div>}
            {!failed && !isVideo && !objectUrl && <div className="text-sm text-white/70">読み込み中…</div>}
            {!isVideo && objectUrl && (
              <img
                src={objectUrl}
                alt={current.originalFileName || current.id}
                className="max-h-[90vh] max-w-[95vw] object-contain select-none"
                draggable={false}
              />
            )}
            {!failed && isVideo && videoUrl && (
              <ReactPlayer
                src={videoUrl}
                controls
                playing={false}
                width="95vw"
                height="90vh"
                style={{ maxHeight: '90vh', maxWidth: '95vw' }}
                onError={() => {
                  toast.error('動画の再生に失敗しました')
                  setFailed(true)
                }}
              />
            )}
          </div>

          {/* 閉じるボタン */}
          <button
            type="button"
            className="absolute top-4 right-4 z-10 rounded-full bg-black/60 p-2 text-white hover:bg-black/80 active:bg-black/90"
            onClick={(e) => {
              e.stopPropagation()
              onOpenChange(false)
            }}
            aria-label="閉じる"
          >
            <X className="size-5" />
          </button>

          <button
            type="button"
            className="absolute top-4 right-16 z-10 rounded-full bg-black/60 p-2 text-white hover:bg-black/80 active:bg-black/90"
            onClick={(e) => {
              e.stopPropagation()
              setShowInfo((visible) => !visible)
            }}
            aria-label={showInfo ? '写真の情報を閉じる' : '写真の情報を表示'}
            aria-expanded={showInfo}
            aria-controls="photo-lightbox-info"
          >
            <Info className="size-5" />
          </button>

          {showInfo && (
            <section
              id="photo-lightbox-info"
              aria-label="写真の情報"
              className="absolute top-16 right-4 z-10 w-[min(20rem,calc(100vw-2rem))] rounded-lg bg-black/80 p-4 text-sm text-white shadow-lg backdrop-blur-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <dl className="space-y-3">
                <div>
                  <dt className="text-white/60">撮影日時</dt>
                  <dd className="mt-0.5">
                    {parseUtcDate(current.fileCreatedAt).toLocaleString('ja-JP', {
                      timeZone: 'Asia/Tokyo',
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </dd>
                </div>
                <div>
                  <dt className="text-white/60">撮影場所</dt>
                  <dd className="mt-0.5">
                    {metadataLoading
                      ? '読み込み中…'
                      : metadataFailed
                        ? '情報を取得できませんでした'
                        : place || (hasCoordinates ? 'GPS情報あり' : '位置情報なし')}
                  </dd>
                  {hasCoordinates && (
                    <dd className="mt-0.5 text-xs text-white/70">
                      {assetMetadata?.latitude}, {assetMetadata?.longitude}
                    </dd>
                  )}
                </div>
                <div>
                  <dt className="text-white/60">カメラ</dt>
                  <dd className="mt-0.5">
                    {metadataLoading
                      ? '読み込み中…'
                      : metadataFailed
                        ? '情報を取得できませんでした'
                        : camera || '情報なし'}
                  </dd>
                </div>
                {isVideo && current.duration !== null && (
                  <div>
                    <dt className="text-white/60">長さ</dt>
                    <dd className="mt-0.5">{formatAssetDuration(current.duration)}</dd>
                  </div>
                )}
              </dl>
            </section>
          )}

          {renderActions && (
            <div className="absolute top-4 left-4 z-10 flex gap-2" onClick={(e) => e.stopPropagation()}>
              {renderActions(current)}
            </div>
          )}

          {/* 前へ / 次へ (2 枚以上のときのみ) */}
          {hasMultiple && (
            <>
              <button
                type="button"
                className="absolute top-1/2 left-3 z-10 -translate-y-1/2 rounded-full bg-black/60 p-3 text-white hover:bg-black/80 active:bg-black/90"
                onClick={(e) => {
                  e.stopPropagation()
                  handlePrev()
                }}
                aria-label="前の写真"
              >
                <ChevronLeft className="size-7" />
              </button>
              <button
                type="button"
                className="absolute top-1/2 right-3 z-10 -translate-y-1/2 rounded-full bg-black/60 p-3 text-white hover:bg-black/80 active:bg-black/90"
                onClick={(e) => {
                  e.stopPropagation()
                  handleNext()
                }}
                aria-label="次の写真"
              >
                <ChevronRight className="size-7" />
              </button>

              {/* インジケータ */}
              <div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
                {index + 1} / {assets.length}
              </div>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
